import { supabase } from './supabaseClient';
import { describeError } from './errorMessage';
import { readOnlyMessage } from './readOnlyNotice';
import type { ReportMetadata } from '../types';
import type { NotrufplanInhalt } from '../components/sige-plan/types';
import {
    aktuelleKatalogVersion,
    ergaenzeHistorie,
    leereHistorischeErkundung,
    leerePersonenkreis,
    leererGeltungsbereich,
} from '../components/asplan/asplanLogic';
import { SCHUTZKLASSEN_VORLAGEN, HANDHABUNG_VORLAGE } from '../components/asplan/kataloge/schutzklassenVorlagen';
// Kalendertag der ORTSZEIT. `toISOString()` liefert das UTC-Datum und
// datiert einen Vorgang kurz nach Mitternacht auf den Vortag zurueck —
// in der Historie des Plans steht dann ein Tag, an dem niemand gearbeitet hat.
import { heuteIso } from '../components/sige-einweisung/einweisungLogic';
import type {
    Asplan,
    AsplanBundle,
    AsplanStatus,
    AsplanUnternehmen,
    BausteinWahl,
    Beteiligter,
    Cluster,
    Probe,
    PruefEintrag,
    Rasterfeld,
    Rolle,
    Schutzklasse,
    Skizze,
    Stufung,
    Taetigkeit,
    Unterlage,
} from '../components/asplan/types';

/**
 * Datenbankzugriff des Moduls „A+S-Plan" (Tabellen `asplaene`, `asplan_*` —
 * supabase/asplan.sql, MIGRATIONS.md Nr. 41). Die einzige Stelle, die mit
 * ihnen redet.
 *
 * Die REGELN liegen daneben in `src/components/asplan/` und sind ohne
 * Datenbank pruefbar; hier steht nur, was ohne Supabase nicht zu haben ist.
 *
 * ═══ VIER HAUSREGELN — wortgleich zu handbuchService.ts ═══════════════════
 *
 * (a) LESEN IST FAIL-OPEN, SCHREIBEN NICHT. Fehlt die Tabelle (Migration
 *     Nr. 41 noch nicht gefahren), liefert das Lesen eine leere Lage statt zu
 *     werfen — dann gibt es eben noch keinen A+S-Plan, und die Kachel oeffnet
 *     sich trotzdem. Jeder schreibende Aufruf meldet den Fehler dagegen
 *     sichtbar.
 *
 * (b) ZWEITE REIHE DES SCHREIBSCHUTZES. Jeder schreibende Aufruf verlangt
 *     einen `SchreibKontext` und prueft ihn selbst. Weil der Kontext ein
 *     Pflichtparameter ist, kann ein neuer Schreibweg die Pruefung nicht
 *     vergessen — der Compiler laesst ihn sonst nicht uebersetzen.
 *
 * (c) KEIN LESEN-DANN-SCHREIBEN. Wo eine Bedingung gilt (nur Entwuerfe
 *     aendern), steht sie als `where`-Bedingung IM UPDATE. Zwischen Lesen und
 *     Schreiben passt eine zweite Sitzung.
 *
 * (d) KEIN AUTOMATISCHES ANLEGEN. `fetchAsplanBundle` legt nichts an. Ein
 *     Projekt ohne A+S-Plan bleibt ohne, bis jemand `ensureAsplan` ausloest.
 *     Dieselbe Ueberlegung wie beim Schreibschutz fuer Nur-Ansicht-Projekte,
 *     wo Selbstheilungs-Migrationen beim reinen Lesen geschrieben hatten.
 */

const T_PLAN = 'asplaene';
const T_FELD = 'asplan_rasterfelder';
const T_PROBE = 'asplan_proben';
const T_CLUSTER = 'asplan_cluster';
const T_TAETIGKEIT = 'asplan_taetigkeiten';
const T_SK = 'asplan_schutzklassen';
const T_ROLLE = 'asplan_rollen';
const T_STUFUNG = 'asplan_stufungen';
const T_BETEILIGT = 'asplan_beteiligte';
const T_UNTERNEHMEN = 'asplan_unternehmen';
const T_UNTERLAGE = 'asplan_unterlagen';
const T_BAUSTEIN = 'asplan_bausteine';
const T_SKIZZE = 'asplan_skizzen';
const T_PRUEF = 'asplan_pruefliste';

/** PostgREST liefert je nach Schema-Cache 42P01 oder PGRST205. */
const errorCode = (e: unknown): string => {
    const c = (e as { code?: unknown })?.code;
    return typeof c === 'string' ? c : '';
};
const istFehlendeTabelle = (e: unknown): boolean => {
    const code = errorCode(e);
    return code === 'PGRST205' || code === '42P01';
};
const istDublette = (e: unknown): boolean => errorCode(e) === '23505';

// ═══════════════════════════════════════════════════════════════════════════
// SCHREIBSCHUTZ — die zweite Reihe
// ═══════════════════════════════════════════════════════════════════════════

export interface SchreibKontext {
    /** Nur-Ansicht: aus „Offene Positionen" geoeffnet oder fremde Projektsperre. */
    nurAnsicht: boolean;
    /** Status des Plans, wie ihn der Container beim Klick kannte. */
    status?: AsplanStatus;
}

const GESPERRT_TEXT = 'Der A+S-Plan ist freigegeben und eingefroren. '
    + 'Für Änderungen bitte eine neue Revision eröffnen; die freigegebene Fassung bleibt erhalten.';

const pruefeSchreibrecht = (ctx: SchreibKontext, was: string): void => {
    if (!ctx || ctx.nurAnsicht) throw new Error(readOnlyMessage(was));
};

const pruefeAenderbar = (ctx: SchreibKontext, was: string): void => {
    pruefeSchreibrecht(ctx, was);
    if (ctx.status === 'freigegeben') throw new Error(GESPERRT_TEXT);
};

// ═══════════════════════════════════════════════════════════════════════════
// ABBILDUNG — Vorgaben fuer das, was die Datenbank NULL-bar haelt
// ═══════════════════════════════════════════════════════════════════════════
//
// Ohne diese Vorgaben steht in einem Textfeld irgendwann „null", und eine
// `.map()` auf einer jsonb-Spalte, die als Objekt statt als Liste
// zurueckkommt, stuerzt die ganze Seite ab. Beides ist im Repo schon passiert.

const alsObjekt = <T>(wert: unknown, vorgabe: T): T =>
    (wert && typeof wert === 'object' && !Array.isArray(wert)) ? (wert as T) : vorgabe;

const alsListe = <T>(wert: unknown): T[] => (Array.isArray(wert) ? (wert as T[]) : []);

const mapPlan = (row: Record<string, unknown>): Asplan => ({
    ...(row as unknown as Asplan),
    veranlassung: alsObjekt(row.veranlassung, {}),
    objekt: alsObjekt(row.objekt, {}),
    historische_erkundung: alsObjekt(row.historische_erkundung, leereHistorischeErkundung()),
    geltungsbereich: alsObjekt(row.geltungsbereich, leererGeltungsbereich()),
    personenkreis: alsObjekt(row.personenkreis, leerePersonenkreis()),
    notfall: alsObjekt(row.notfall, {}),
    messkonzept: alsListe(row.messkonzept),
    grenzwerte: alsListe(row.grenzwerte),
    stoffe: alsListe(row.stoffe),
    regelwerke: alsListe(row.regelwerke),
    historie: alsListe(row.historie),
});

const mapProbe = (row: Record<string, unknown>): Probe => ({
    ...(row as unknown as Probe),
    befunde: alsListe(row.befunde),
});

const mapCluster = (row: Record<string, unknown>): Cluster => ({
    ...(row as unknown as Cluster),
    felder: alsListe(row.felder),
    umgebung: alsObjekt(row.umgebung, {}),
    sige_arbeitsschritt_ids: alsListe(row.sige_arbeitsschritt_ids),
});

const mapTaetigkeit = (row: Record<string, unknown>): Taetigkeit => ({
    ...(row as unknown as Taetigkeit),
    stoffe: alsListe(row.stoffe),
    expositionswege: alsListe(row.expositionswege),
    emission: alsObjekt(row.emission, {}),
    massnahmen: alsListe(row.massnahmen),
});

const mapSchutzklasse = (row: Record<string, unknown>): Schutzklasse => ({
    ...(row as unknown as Schutzklasse),
    psa: alsListe(row.psa),
    zusatz: alsListe(row.zusatz),
    atemschutz: alsObjekt(row.atemschutz, {}),
    schutzkleidung: alsObjekt(row.schutzkleidung, {}),
    handschutz: alsObjekt(row.handschutz, {}),
    fussschutz: alsObjekt(row.fussschutz, {}),
    tragezeit: alsObjekt(row.tragezeit, {}),
    handhabung: alsListe(row.handhabung),
});

const mapRolle = (row: Record<string, unknown>): Rolle => ({
    ...(row as unknown as Rolle),
    cluster: alsListe(row.cluster),
    schritte: alsListe(row.schritte),
    situationen: alsListe(row.situationen),
});

const mapBeteiligter = (row: Record<string, unknown>): Beteiligter => ({
    ...(row as unknown as Beteiligter),
    gilt_fuer: alsListe(row.gilt_fuer),
});

const mapSkizze = (row: Record<string, unknown>): Skizze => ({
    ...(row as unknown as Skizze),
    parameter: alsObjekt(row.parameter, {}),
});

// ═══════════════════════════════════════════════════════════════════════════
// KOPFSATZ
// ═══════════════════════════════════════════════════════════════════════════

export const fetchAsplan = async (projectId: string): Promise<Asplan | null> => {
    if (!projectId || projectId === 'temp-id') return null;
    const { data, error } = await supabase
        .from(T_PLAN).select('*').eq('project_id', projectId).maybeSingle();

    if (error) {
        if (istFehlendeTabelle(error)) return null;
        throw new Error(describeError(error));
    }
    return data ? mapPlan(data) : null;
};

/**
 * Legt den Plan an — mit den Schutzklassen-Vorlagen als Startbestand.
 *
 * Die Vorlagen werden KOPIERT und nicht referenziert: ab dem Anlegen gehoeren
 * sie diesem Plan und aendern sich nicht mehr mit dem Katalog. Genau das ist
 * der Sinn von `katalog_version` am Datensatz.
 */
export const ensureAsplan = async (
    ctx: SchreibKontext,
    projectId: string,
    titel: string,
): Promise<Asplan> => {
    pruefeSchreibrecht(ctx, 'einen A+S-Plan anzulegen');
    if (!projectId || projectId === 'temp-id') {
        throw new Error('Ohne gespeichertes Projekt kann kein A+S-Plan angelegt werden.');
    }

    const vorhanden = await fetchAsplan(projectId);
    if (vorhanden) return vorhanden;

    const { data, error } = await supabase
        .from(T_PLAN)
        .insert({
            project_id: projectId,
            titel: titel || 'Arbeits- und Sicherheitsplan (A+S-Plan)',
            untertitel: 'gemäß DGUV Regel 101-004 / TRGS 524',
            regelwerk: 'dguv101_004',
            regelwerk_fassung: '2026-07',
            status: 'entwurf',
            katalog_version: aktuelleKatalogVersion(),
            historische_erkundung: leereHistorischeErkundung(),
            geltungsbereich: leererGeltungsbereich(),
            personenkreis: leerePersonenkreis(),
            veranlassung: {}, objekt: {}, notfall: {},
            messkonzept: [], grenzwerte: [], stoffe: [], regelwerke: [], historie: [],
        })
        .select('*')
        .single();

    if (error) {
        if (istDublette(error)) {
            const jetzt = await fetchAsplan(projectId);
            if (jetzt) return jetzt;
        }
        throw new Error(describeError(error));
    }

    const plan = mapPlan(data);
    await legeSchutzklassenAn(ctx, plan);
    return plan;
};

/** Kopiert die Katalogvorlagen als Schutzklassen dieses Plans. */
const legeSchutzklassenAn = async (ctx: SchreibKontext, plan: Asplan): Promise<void> => {
    const zeilen = SCHUTZKLASSEN_VORLAGEN.map((v, i) => ({
        project_id: plan.project_id,
        asplan_id: plan.id,
        key: v.key,
        rang: v.rang,
        bezeichnung: v.bezeichnung,
        anwendungsbereich: v.anwendungsbereich,
        psa: v.psa,
        zusatz: v.zusatz,
        atemschutz: v.atemschutz,
        schutzkleidung: v.schutzkleidung,
        handschutz: v.handschutz,
        fussschutz: v.fussschutz,
        tragezeit: {},
        handhabung: v.rang > 1 ? HANDHABUNG_VORLAGE : [],
        farbe: v.farbe,
        sortierung: i,
    }));

    const { error } = await supabase.from(T_SK).insert(zeilen);
    // Fehlt die Tabelle oder gibt es die Klassen schon, ist das kein Grund,
    // den ganzen Anlegevorgang zurueckzuweisen — der Plan steht bereits.
    if (error && !istFehlendeTabelle(error) && !istDublette(error)) {
        throw new Error(describeError(error));
    }
};

/**
 * Aendert den Kopfsatz.
 *
 * Der Riegel steht ZWEIMAL: `pruefeAenderbar` im Client und
 * `.eq('status','entwurf')` im UPDATE. Der zweite ist der wirksame — zwischen
 * dem Laden der Seite und dem Klick kann jemand anderes freigegeben haben.
 */
export const updateAsplan = async (
    ctx: SchreibKontext,
    id: string,
    updates: Partial<Asplan>,
): Promise<Asplan> => {
    pruefeAenderbar(ctx, 'den A+S-Plan zu ändern');

    const { data, error } = await supabase
        .from(T_PLAN)
        .update(updates)
        .eq('id', id)
        .eq('status', 'entwurf')
        .select('*')
        .maybeSingle();

    if (error) throw new Error(describeError(error));
    if (!data) throw new Error(GESPERRT_TEXT);
    return mapPlan(data);
};

/** Gibt den Plan frei und friert ihn ein. */
export const freigebenAsplan = async (
    ctx: SchreibKontext,
    plan: Asplan,
    von: string,
    revLabel: string,
): Promise<Asplan> => {
    pruefeAenderbar(ctx, 'den A+S-Plan freizugeben');

    const heute = heuteIso();
    const { data, error } = await supabase
        .from(T_PLAN)
        .update({
            status: 'freigegeben',
            rev_label: revLabel,
            freigegeben_am: new Date().toISOString(),
            freigegeben_von_name: von,
            historie: ergaenzeHistorie(plan.historie, {
                datum: heute,
                autor: von,
                aenderungen: 'Freigabe',
                rev_label: revLabel,
            }),
        })
        .eq('id', plan.id)
        .eq('status', 'entwurf')
        .select('*')
        .maybeSingle();

    if (error) throw new Error(describeError(error));
    if (!data) throw new Error('Der Plan ist bereits freigegeben.');
    return mapPlan(data);
};

/**
 * Eroeffnet eine neue Revision.
 *
 * Die freigegebene Fassung wird NICHT ueberschrieben: ihr PDF liegt in der
 * Projektablage, und die Historie behaelt ihren Eintrag. Hier wird nur der
 * Status zurueckgesetzt, damit weitergearbeitet werden kann.
 */
export const neueRevision = async (
    ctx: SchreibKontext,
    plan: Asplan,
    von: string,
    grund: string,
): Promise<Asplan> => {
    pruefeSchreibrecht(ctx, 'eine neue Revision zu eröffnen');

    const heute = heuteIso();
    const { data, error } = await supabase
        .from(T_PLAN)
        .update({
            status: 'entwurf',
            freigegeben_am: null,
            freigegeben_von_name: '',
            historie: ergaenzeHistorie(plan.historie, {
                datum: heute, autor: von, aenderungen: grund || 'Neue Revision eröffnet',
            }),
        })
        .eq('id', plan.id)
        .select('*')
        .single();

    if (error) throw new Error(describeError(error));
    return mapPlan(data);
};

// ═══════════════════════════════════════════════════════════════════════════
// KINDTABELLEN — ein Weg fuer alle
// ═══════════════════════════════════════════════════════════════════════════
//
// Vierzehn Tabellen mit demselben Zuschnitt (project_id, asplan_id,
// sortierung) rechtfertigen keine vierzehn Kopien desselben CRUD. Der
// Schreibschutz bleibt trotzdem an jedem Aufruf: `ctx` ist Pflichtparameter.

type Kind = { id: string; project_id: string; asplan_id: string };

const ladeKinder = async <T>(
    tabelle: string,
    asplanId: string,
    map: (row: Record<string, unknown>) => T,
    sortSpalte = 'sortierung',
): Promise<T[]> => {
    const { data, error } = await supabase
        .from(tabelle).select('*').eq('asplan_id', asplanId).order(sortSpalte, { ascending: true });

    if (error) {
        if (istFehlendeTabelle(error)) return [];
        throw new Error(describeError(error));
    }
    return (data ?? []).map(map);
};

const legeAn = async <T extends Kind>(
    ctx: SchreibKontext,
    tabelle: string,
    was: string,
    zeile: Omit<T, 'id'> & Record<string, unknown>,
    map: (row: Record<string, unknown>) => T,
): Promise<T> => {
    pruefeAenderbar(ctx, was);
    const { data, error } = await supabase.from(tabelle).insert(zeile).select('*').single();
    if (error) throw new Error(describeError(error));
    return map(data);
};

const aendere = async <T>(
    ctx: SchreibKontext,
    tabelle: string,
    was: string,
    id: string,
    updates: Record<string, unknown>,
    map: (row: Record<string, unknown>) => T,
): Promise<T> => {
    pruefeAenderbar(ctx, was);
    const { data, error } = await supabase
        .from(tabelle).update(updates).eq('id', id).select('*').single();
    if (error) throw new Error(describeError(error));
    return map(data);
};

const loesche = async (
    ctx: SchreibKontext,
    tabelle: string,
    was: string,
    id: string,
): Promise<void> => {
    pruefeAenderbar(ctx, was);
    const { error } = await supabase.from(tabelle).delete().eq('id', id);
    if (error) throw new Error(describeError(error));
};

const durchreichen = <T>(row: Record<string, unknown>): T => row as unknown as T;

// ── Rasterfelder ───────────────────────────────────────────────────────────

export const createRasterfeld = (ctx: SchreibKontext, zeile: Omit<Rasterfeld, 'id'>) =>
    legeAn<Rasterfeld>(ctx, T_FELD, 'ein Rasterfeld anzulegen', zeile, durchreichen);
export const updateRasterfeld = (ctx: SchreibKontext, id: string, u: Partial<Rasterfeld>) =>
    aendere<Rasterfeld>(ctx, T_FELD, 'ein Rasterfeld zu ändern', id, u, durchreichen);
export const deleteRasterfeld = (ctx: SchreibKontext, id: string) =>
    loesche(ctx, T_FELD, 'ein Rasterfeld zu löschen', id);

// ── Proben ─────────────────────────────────────────────────────────────────

export const createProbe = (ctx: SchreibKontext, zeile: Omit<Probe, 'id'>) =>
    legeAn<Probe>(ctx, T_PROBE, 'eine Probe anzulegen', zeile, mapProbe);
export const updateProbe = (ctx: SchreibKontext, id: string, u: Partial<Probe>) =>
    aendere<Probe>(ctx, T_PROBE, 'eine Probe zu ändern', id, u, mapProbe);
export const deleteProbe = (ctx: SchreibKontext, id: string) =>
    loesche(ctx, T_PROBE, 'eine Probe zu löschen', id);

// ── Arbeitsbereiche ────────────────────────────────────────────────────────

export const createCluster = (ctx: SchreibKontext, zeile: Omit<Cluster, 'id'>) =>
    legeAn<Cluster>(ctx, T_CLUSTER, 'einen Arbeitsbereich anzulegen', zeile, mapCluster);
export const updateCluster = (ctx: SchreibKontext, id: string, u: Partial<Cluster>) =>
    aendere<Cluster>(ctx, T_CLUSTER, 'einen Arbeitsbereich zu ändern', id, u, mapCluster);
export const deleteCluster = (ctx: SchreibKontext, id: string) =>
    loesche(ctx, T_CLUSTER, 'einen Arbeitsbereich zu löschen', id);

// ── Taetigkeiten ───────────────────────────────────────────────────────────

export const createTaetigkeit = (ctx: SchreibKontext, zeile: Omit<Taetigkeit, 'id'>) =>
    legeAn<Taetigkeit>(ctx, T_TAETIGKEIT, 'eine Tätigkeit anzulegen', zeile, mapTaetigkeit);
export const updateTaetigkeit = (ctx: SchreibKontext, id: string, u: Partial<Taetigkeit>) =>
    aendere<Taetigkeit>(ctx, T_TAETIGKEIT, 'eine Tätigkeit zu ändern', id, u, mapTaetigkeit);
export const deleteTaetigkeit = (ctx: SchreibKontext, id: string) =>
    loesche(ctx, T_TAETIGKEIT, 'eine Tätigkeit zu löschen', id);

// ── Schutzklassen ──────────────────────────────────────────────────────────

export const createSchutzklasse = (ctx: SchreibKontext, zeile: Omit<Schutzklasse, 'id'>) =>
    legeAn<Schutzklasse>(ctx, T_SK, 'eine Schutzklasse anzulegen', zeile, mapSchutzklasse);
export const updateSchutzklasse = (ctx: SchreibKontext, id: string, u: Partial<Schutzklasse>) =>
    aendere<Schutzklasse>(ctx, T_SK, 'eine Schutzklasse zu ändern', id, u, mapSchutzklasse);
export const deleteSchutzklasse = (ctx: SchreibKontext, id: string) =>
    loesche(ctx, T_SK, 'eine Schutzklasse zu löschen', id);

// ── Rollen ─────────────────────────────────────────────────────────────────

export const createRolle = (ctx: SchreibKontext, zeile: Omit<Rolle, 'id'>) =>
    legeAn<Rolle>(ctx, T_ROLLE, 'eine Rolle anzulegen', zeile, mapRolle);
export const updateRolle = (ctx: SchreibKontext, id: string, u: Partial<Rolle>) =>
    aendere<Rolle>(ctx, T_ROLLE, 'eine Rolle zu ändern', id, u, mapRolle);
export const deleteRolle = (ctx: SchreibKontext, id: string) =>
    loesche(ctx, T_ROLLE, 'eine Rolle zu löschen', id);

// ── Stufungen ──────────────────────────────────────────────────────────────
//
// Eine Stufung wird NIE geloescht und nie geaendert, nachdem sie bestaetigt
// ist: sie ist ein Vorgang, kein Stammdatensatz. Deshalb gibt es hier
// bewusst kein `deleteStufung`.

export const createStufung = (ctx: SchreibKontext, zeile: Omit<Stufung, 'id'>) =>
    legeAn<Stufung>(ctx, T_STUFUNG, 'eine Auf-/Abstufung zu erfassen', zeile, durchreichen);
export const updateStufung = (ctx: SchreibKontext, id: string, u: Partial<Stufung>) =>
    aendere<Stufung>(ctx, T_STUFUNG, 'eine Auf-/Abstufung zu ändern', id, u, durchreichen);

// ── Beteiligte, Unternehmen, Unterlagen ────────────────────────────────────

export const createBeteiligter = (ctx: SchreibKontext, zeile: Omit<Beteiligter, 'id'>) =>
    legeAn<Beteiligter>(ctx, T_BETEILIGT, 'einen Beteiligten anzulegen', zeile, mapBeteiligter);
export const updateBeteiligter = (ctx: SchreibKontext, id: string, u: Partial<Beteiligter>) =>
    aendere<Beteiligter>(ctx, T_BETEILIGT, 'einen Beteiligten zu ändern', id, u, mapBeteiligter);
export const deleteBeteiligter = (ctx: SchreibKontext, id: string) =>
    loesche(ctx, T_BETEILIGT, 'einen Beteiligten zu löschen', id);

export const createUnternehmen = (ctx: SchreibKontext, zeile: Omit<AsplanUnternehmen, 'id'>) =>
    legeAn<AsplanUnternehmen>(ctx, T_UNTERNEHMEN, 'ein Unternehmen anzulegen', zeile, durchreichen);
export const updateUnternehmen = (ctx: SchreibKontext, id: string, u: Partial<AsplanUnternehmen>) =>
    aendere<AsplanUnternehmen>(ctx, T_UNTERNEHMEN, 'ein Unternehmen zu ändern', id, u, durchreichen);
export const deleteUnternehmen = (ctx: SchreibKontext, id: string) =>
    loesche(ctx, T_UNTERNEHMEN, 'ein Unternehmen zu löschen', id);

export const createUnterlage = (ctx: SchreibKontext, zeile: Omit<Unterlage, 'id'>) =>
    legeAn<Unterlage>(ctx, T_UNTERLAGE, 'eine Unterlage anzulegen', zeile, durchreichen);
export const updateUnterlage = (ctx: SchreibKontext, id: string, u: Partial<Unterlage>) =>
    aendere<Unterlage>(ctx, T_UNTERLAGE, 'eine Unterlage zu ändern', id, u, durchreichen);
export const deleteUnterlage = (ctx: SchreibKontext, id: string) =>
    loesche(ctx, T_UNTERLAGE, 'eine Unterlage zu löschen', id);

// ── Bausteine, Skizzen, Pruefliste ─────────────────────────────────────────

export const createBaustein = (ctx: SchreibKontext, zeile: Omit<BausteinWahl, 'id'>) =>
    legeAn<BausteinWahl>(ctx, T_BAUSTEIN, 'einen Textbaustein zu wählen', zeile, durchreichen);
export const updateBaustein = (ctx: SchreibKontext, id: string, u: Partial<BausteinWahl>) =>
    aendere<BausteinWahl>(ctx, T_BAUSTEIN, 'einen Textbaustein zu ändern', id, u, durchreichen);

export const createSkizze = (ctx: SchreibKontext, zeile: Omit<Skizze, 'id'>) =>
    legeAn<Skizze>(ctx, T_SKIZZE, 'eine Skizze anzulegen', zeile, mapSkizze);
export const updateSkizze = (ctx: SchreibKontext, id: string, u: Partial<Skizze>) =>
    aendere<Skizze>(ctx, T_SKIZZE, 'eine Skizze zu ändern', id, u, mapSkizze);
export const deleteSkizze = (ctx: SchreibKontext, id: string) =>
    loesche(ctx, T_SKIZZE, 'eine Skizze zu löschen', id);

/**
 * Setzt den Stand eines Pruefpunkts.
 *
 * `upsert` auf (asplan_id, check_key): der Punkt existiert erst, wenn ihn
 * jemand beurteilt hat. Ein leerer Bestand heisst „noch nichts geprueft" und
 * nicht „alles offen erfasst" — den Unterschied sieht man auf der Kachel.
 */
export const setzePruefstand = async (
    ctx: SchreibKontext,
    zeile: Omit<PruefEintrag, 'id'>,
): Promise<PruefEintrag> => {
    pruefeAenderbar(ctx, 'einen Prüfpunkt zu beurteilen');
    const { data, error } = await supabase
        .from(T_PRUEF)
        .upsert(zeile, { onConflict: 'asplan_id,check_key' })
        .select('*')
        .single();
    if (error) throw new Error(describeError(error));
    return data as unknown as PruefEintrag;
};

// ═══════════════════════════════════════════════════════════════════════════
// ALLES AUF EINMAL
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Laedt den vollstaendigen Plan.
 *
 * Alle Kindtabellen parallel: sie haengen nur am Plan, nicht aneinander. Bei
 * fehlender Tabelle liefert jede Teilabfrage eine leere Liste (fail-open),
 * sodass die Kachel sich auch ohne Migration Nr. 41 oeffnet — dann eben leer.
 */
export const fetchAsplanBundle = async (projectId: string): Promise<AsplanBundle | null> => {
    const plan = await fetchAsplan(projectId);
    if (!plan) return null;

    const [
        rasterfelder, proben, cluster, taetigkeiten, schutzklassen, rollen,
        stufungen, beteiligte, unternehmen, unterlagen, bausteine, skizzen, pruefliste,
    ] = await Promise.all([
        ladeKinder<Rasterfeld>(T_FELD, plan.id, durchreichen),
        ladeKinder<Probe>(T_PROBE, plan.id, mapProbe),
        ladeKinder<Cluster>(T_CLUSTER, plan.id, mapCluster),
        ladeKinder<Taetigkeit>(T_TAETIGKEIT, plan.id, mapTaetigkeit),
        ladeKinder<Schutzklasse>(T_SK, plan.id, mapSchutzklasse),
        ladeKinder<Rolle>(T_ROLLE, plan.id, mapRolle),
        ladeKinder<Stufung>(T_STUFUNG, plan.id, durchreichen, 'entschieden_am'),
        ladeKinder<Beteiligter>(T_BETEILIGT, plan.id, mapBeteiligter),
        ladeKinder<AsplanUnternehmen>(T_UNTERNEHMEN, plan.id, durchreichen),
        ladeKinder<Unterlage>(T_UNTERLAGE, plan.id, durchreichen),
        ladeKinder<BausteinWahl>(T_BAUSTEIN, plan.id, durchreichen),
        ladeKinder<Skizze>(T_SKIZZE, plan.id, mapSkizze),
        ladeKinder<PruefEintrag>(T_PRUEF, plan.id, durchreichen, 'check_key'),
    ]);

    return {
        plan, rasterfelder, proben, cluster, taetigkeiten, schutzklassen, rollen,
        stufungen, beteiligte, unternehmen, unterlagen, bausteine, skizzen, pruefliste,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// DER BESTAND — was das Modul aus dem Projekt LIEST
// ═══════════════════════════════════════════════════════════════════════════
//
// Beide Abfragen sind reine Lesewege und fail-open (Hausregel a): ein Projekt
// ohne SiGe-Plan, ohne Notrufplan oder ohne Vertreterliste ist kein Fehler,
// sondern der Normalfall am ersten Tag. Was hier zurueckkommt, wird NIE von
// selbst geschrieben — die Uebernahme laeuft auf Knopfdruck
// (`components/asplan/bestandUebernahme.ts`).

/**
 * Die Projektstammdaten (`projects.data.metadata`).
 *
 * EIN Lesevorgang fuer alles, was das Modul aus dem Projekt uebernimmt:
 * Vertreterliste, Bauherr, Auftraggeber und die Bauleitung vor Ort. Die
 * Ableitungen daraus stehen ohne Datenbank in
 * `components/asplan/bestandUebernahme.ts` — insbesondere
 * `getRepresentatives`, die einzige Ableitung der Vertreterliste im Repo:
 * sie kennt `metadata.representatives` UND die Legacy-Felder `coordinator*`
 * der Alt-Projekte. Wer sie nachbaute, bekaeme bei Alt-Projekten eine leere
 * Liste und schlosse daraus, es gebe keinen Koordinator.
 */
export const fetchProjektMetadaten = async (
    projectId: string,
): Promise<Partial<ReportMetadata> | null> => {
    if (!projectId || projectId === 'temp-id') return null;

    const { data, error } = await supabase
        .from('projects').select('data').eq('id', projectId).maybeSingle();
    if (error || !data) return null;

    const roh = (data as { data?: { metadata?: ReportMetadata } }).data;
    return alsObjekt<Partial<ReportMetadata> | null>(roh?.metadata, null);
};

/**
 * Der Notrufplan des SiGe-Plans — NUR gelesen.
 *
 * Es gibt genau einen Notrufplan je Projekt, und der gehoert dem SiGe-Plan
 * (Plan Abschnitt 10: „lesen — EINE Quelle"). Klinik und Durchgangsarzt in
 * die asplan-Tabellen zu kopieren hiesse, sie ab dem naechsten Wechsel an
 * zwei Stellen zu pflegen — und eine veraltete Notfallnummer ist schlimmer
 * als eine fehlende, weil sie im Ernstfall niemanden stutzig macht.
 *
 * Gelesen wird derselbe Weg wie im Handbuch (`useHandbuch.ts`): der jueng-
 * ste, nicht geloeschte SiGe-Plan, darin der Block `typ = 'notrufplan'`.
 */
export const fetchNotrufplan = async (
    projectId: string,
): Promise<NotrufplanInhalt | null> => {
    if (!projectId || projectId === 'temp-id') return null;

    const { data: plaene, error } = await supabase
        .from('sige_projekte')
        .select('id')
        .eq('project_id', projectId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(1);
    if (error) return null;

    const sigeId = ((plaene || [])[0] as { id?: string } | undefined)?.id;
    if (!sigeId) return null;

    const { data: bloecke } = await supabase
        .from('sige_bloecke')
        .select('typ, inhalt')
        .eq('sige_projekt_id', sigeId)
        .eq('typ', 'notrufplan')
        .limit(1);

    const inhalt = ((bloecke || [])[0] as { inhalt?: unknown } | undefined)?.inhalt;
    return alsObjekt<NotrufplanInhalt | null>(inhalt, null);
};
